import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import i18n from 'i18next';
import { useTranslation } from "react-i18next";
import { CSSTransition } from 'react-transition-group';
import { Tooltip } from 'antd';
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import Flex from "../../core/Flex";
import useAdminApplicationDetailStore from "../../../store/useAdminApplicationDetailStore";
import useAuthAdminStore from "../../../store/useAuthAdminStore";
import FormTitle from "../../form/FormTitle";
import FormText from "../../form/FormText";
import FormRemark from "../../form/FormRemark";
import FormButtonPrimary from "../../form/FormButtonPrimary";
import FormButtonDanger from "../../form/FormButtonDanger";
import FormButtonSecondary from "../../form/FormButtonSecondary";
import Divider from "../../core/Divider";
import formatCurrency from "../../../function/formatCurrency";
import useModalStore from "../../../store/useModalStore";
import { ContentApprove1, ContentClaimRequest } from "../../admin/application/ApplicationInfo";
import { ContentComposeEmail } from "../../admin/application/Email";
import withErrorHandling from "../../../function/withErrorHandling";
import downloadBase64File from "../../../function/downloadBase64File";

function Claim() {
  const { t } = useTranslation();
  const { aid } = useParams();
  const { intstatus, data, contentComposeEmail_Reason_List, openEmailTemplate, admUpdatePreAuthClaim, admGetPreAuthForm, openContentScheduledChargeback } = useAdminApplicationDetailStore();
  const { setContentApprove1_TreatmentType, setContentApprove1_ClassificationOfSurgery, setContentApprove1_ICD, setContentApprove1_ICD_code, setContentApprove1_Remarks, setContentApprove1_RemarksText, setContentApprove1_savingAmount } = useAdminApplicationDetailStore();
  const { setContentClaimRequest_Incurred_amount, setContentClaimRequest_paid_amount, setContentClaimRequest_chargeback, setContentClaimRequest_wis_no, setContentClaimRequest_debit_no } = useAdminApplicationDetailStore();
  const { setIsValid_contentClaimRequest_Incurred_amount, setIsValid_contentClaimRequest_paid_amount, setIsValid_contentClaimRequest_chargeback, setIsValid_contentClaimRequest_wis_no, setIsValid_contentClaimRequest_debit_no } = useAdminApplicationDetailStore();
  const { contentApprove1_TreatmentType_List, contentApprove1_ClassificationOfSurgery_List } = useAdminApplicationDetailStore();
  const { admGeneratePDF } = useAdminApplicationDetailStore();
  const { openModalConfirm, openModalInfo, closeModalConfirm } = useModalStore();
  const { isCSTeam } = useAuthAdminStore();

  const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
  const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
  const loadEmailTemplate = withErrorHandling(openEmailTemplate);

  const getProgrssStatus = ({
    step,
    intstatus,
    flowType,
  }) => {
    return <Flex gap={8}>
      {(step === 1 && (intstatus === "APPROVELOG")) && <IconProgress>1</IconProgress>}
      {(step === 1 && (intstatus === "PENDCHARGE" || intstatus === "SCHCHARGE" || intstatus === "CHARGESETTLE" || intstatus === "OUTCHARGE" || intstatus === "CLAIMSETTLE")) && <Icon20 src={`${process.env.PUBLIC_URL}/images/tick_circle_light_blue.png`} alt="icon" />}
      {(step === 2 && (intstatus === "APPROVELOG" || intstatus === "PENDCHARGE" || intstatus === "SCHCHARGE" || intstatus === "OUTCHARGE")) && <IconProgress>2</IconProgress>}
      {(step === 2 && (intstatus === "CHARGESETTLE" || intstatus === "CLAIMSETTLE")) && <Icon20 src={`${process.env.PUBLIC_URL}/images/tick_circle_light_blue.png`} alt="icon" />}
      {(step === 3 && (intstatus === "APPROVELOG" || intstatus === "PENDCHARGE" || intstatus === "SCHCHARGE" || intstatus === "OUTCHARGE" || intstatus === "CHARGESETTLE")) && <IconProgress>3</IconProgress>}
      {(step === 3 && (intstatus === "CLAIMSETTLE")) && <Icon20 src={`${process.env.PUBLIC_URL}/images/tick_circle_light_blue.png`} alt="icon" />}

      <Flex direction={"column"} gap={0}>
        {step === 1 && <FormText style={{ fontSize: 12, lineHeight: "18px" }}>{t("translation:progress.status.hospitalisation_or_surgery")}</FormText>}
        {step === 2 && <FormText style={{ fontSize: 12, lineHeight: "18px" }}>{t("translation:progress.status.claim_progressing")}</FormText>}
        {step === 3 && <FormText style={{ fontSize: 12, lineHeight: "18px" }}>{t("translation:progress.status.claim_result")}</FormText>}

        {(step === 1 && (intstatus === "APPROVELOG")) && <TextBlue>{t("translation:progress.status.pending")}</TextBlue>}
        {(step === 1 && (intstatus === "PENDCHARGE" || intstatus === "SCHCHARGE" || intstatus === "CHARGESETTLE" || intstatus === "OUTCHARGE" || intstatus === "CLAIMSETTLE")) && <TextGreen>{t("translation:progress.status.completed")}</TextGreen>}
        {(step === 2 && (intstatus === "PENDCHARGE" || intstatus === "SCHCHARGE")) && <TextBlue>{t("translation:progress.status.confirming")}</TextBlue>}
        {(step === 2 && (intstatus === "OUTCHARGE")) && <TextRed>{t("translation:progress.status.failed_to_chargeback")}</TextRed>}
        {(step === 2 && (intstatus === "CHARGESETTLE" || intstatus === "CLAIMSETTLE")) && <TextGreen>{t("translation:progress.status.chargeback_completed")}</TextGreen>}
        {(step === 3 && (intstatus === "OUTCHARGE")) && <TextRed>{t("translation:progress.status.settlement_failed")}</TextRed>}
        {(step === 3 && (intstatus === "CLAIMSETTLE")) && <TextGreen>{t("translation:progress.status.claim_settled")}</TextGreen>}
      </Flex>
    </Flex>
  };

  const getRemarkString = (remarkOption, remarkString) => {
    const stringArray = [];
    const remarkOptionArray = remarkOption?.split(",");

    if (remarkOptionArray?.length > 0) {
      remarkOptionArray.forEach((item) => {
        const itemString = contentComposeEmail_Reason_List.find((reason) => reason.code === item.value)?.label;
        stringArray.push(itemString);
      });
    }

    if (remarkString) {
      stringArray.push(remarkString);

    }

    return stringArray.join(", ");
  };

  const openContentApprove1Modal = (value) => {
    setContentApprove1_TreatmentType(contentApprove1_TreatmentType_List.find(item => item.value === data?.claim?.treatmentType));
    setContentApprove1_ClassificationOfSurgery(contentApprove1_ClassificationOfSurgery_List.find(item => item.value === data?.claim?.classificationSurgery));
    setContentApprove1_ICD(data?.claim?.ICDCode);
    setContentApprove1_ICD_code(data?.claim?.ICDCodeList?.split(",") || []);
    setContentApprove1_Remarks(data?.claim?.treatmentRemarkOption?.split(","));
    setContentApprove1_RemarksText(data?.claim?.treatmentRemark);
    setContentApprove1_savingAmount(data?.claim?.saveAmt);

    openModalConfirm({
      title: "Approve Request",
      modalConfirmPrimaryButtonText: "Save",
      modalConfirmSecondaryButtonText: "Back",
      content: <ContentApprove1 />,
      style: { content: { width: 720 } },
      callback: async () => {
        const { contentApprove1_TreatmentType, contentApprove1_ClassificationOfSurgery, contentApprove1_ICD, contentApprove1_ICD_code, contentApprove1_Remarks, contentApprove1_Remarks_text, contentApprove1_savingAmount } = useAdminApplicationDetailStore.getState();

        await loadAdmUpdatePreAuthClaim(aid, {
          treatmentType: contentApprove1_TreatmentType?.value ?? "",
          classificationSurgery: contentApprove1_ClassificationOfSurgery?.value ?? "",
          ICDCode: contentApprove1_ICD ?? "",
          ICDCodeList: contentApprove1_ICD_code?.join(","),
          treatmentRemarkOption: contentApprove1_Remarks?.join(","),
          treatmentRemark: contentApprove1_Remarks_text ?? "",
          saveAmt: contentApprove1_savingAmount ?? "",
        });
        await loadAdmGetPreAuthForm(aid);
      },
    });
  };

  const openContentClaimRequest = async (value) => {
    setContentClaimRequest_Incurred_amount(data?.claim?.incurredAmt);
    setContentClaimRequest_paid_amount(data?.claim?.paidAmt);
    setContentClaimRequest_chargeback(data?.claim?.chargeBack);
    setContentClaimRequest_wis_no(data?.claim?.WISNo);
    setContentClaimRequest_debit_no(data?.claim?.debitno);
    setIsValid_contentClaimRequest_Incurred_amount(true);
    setIsValid_contentClaimRequest_paid_amount(true);
    setIsValid_contentClaimRequest_chargeback(true);
    setIsValid_contentClaimRequest_wis_no(true);
    setIsValid_contentClaimRequest_debit_no(true);

    openModalConfirm({
      title: "Claim Request",
      modalConfirmPrimaryButtonText: "Save",
      modalConfirmSecondaryButtonText: "Back",
      content: <ContentClaimRequest />,
      closeManually: true,
      style: { content: { width: 720 } },
      callback: async () => {
        const { contentClaimRequest_Incurred_amount, contentClaimRequest_paid_amount, contentClaimRequest_chargeback, contentClaimRequest_wis_no, contentClaimRequest_debit_no } = useAdminApplicationDetailStore.getState();
        const { setIsValid_contentClaimRequest_Incurred_amount, setIsValid_contentClaimRequest_paid_amount, setIsValid_contentClaimRequest_chargeback, setIsValid_contentClaimRequest_wis_no, setIsValid_contentClaimRequest_debit_no } = useAdminApplicationDetailStore.getState();

        setIsValid_contentClaimRequest_Incurred_amount(true);
        setIsValid_contentClaimRequest_paid_amount(true);
        setIsValid_contentClaimRequest_chargeback(true);
        setIsValid_contentClaimRequest_wis_no(true);
        setIsValid_contentClaimRequest_debit_no(true);

        let isValid = true;

        if (!contentClaimRequest_Incurred_amount) {
          setIsValid_contentClaimRequest_Incurred_amount(false);
          isValid = false;
        }

        if (!contentClaimRequest_paid_amount) {
          setIsValid_contentClaimRequest_paid_amount(false);
          isValid = false;
        }

        if (!contentClaimRequest_chargeback) {
          setIsValid_contentClaimRequest_chargeback(false);
          isValid = false;
        }

        if (!contentClaimRequest_wis_no) {
          setIsValid_contentClaimRequest_wis_no(false);
          isValid = false;
        }

        if (!contentClaimRequest_debit_no) {
          setIsValid_contentClaimRequest_debit_no(false);
          isValid = false;
        }

        if (isValid) {
          closeModalConfirm();

          await loadAdmUpdatePreAuthClaim(aid, {
            incurredAmt: contentClaimRequest_Incurred_amount,
            paidAmt: contentClaimRequest_paid_amount,
            chargeBack: contentClaimRequest_chargeback,
            WISNo: contentClaimRequest_wis_no,
            debitno: contentClaimRequest_debit_no,
          });
          await loadAdmGetPreAuthForm(aid);
        }
      },
      cancelCallback: () => {

      },
    });
  };

  const loadAdmGeneratePDF = withErrorHandling(admGeneratePDF);

  return (
    <Flex direction={"column"} alignItems={"flex-start"} gap={24} height={"100%"} width={"100%"} style={{ overflow: "auto" }}>
      <Flex gap={8} justifyContent={"space-between"} width={"100%"} style={{ paddingTop: 5, paddingRight: 5 }}>
        <Flex>
          <Icon32 src={`${process.env.PUBLIC_URL}/images/medical-board-2.png`} alt={t("translation:admin.request.tab.claim.content_title")} />
          <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>{t("translation:admin.request.tab.claim.content_title")}</FormTitle>
        </Flex>

        <Flex>
          <DownloadBtn onClick={async () => {
            const file = await loadAdmGeneratePDF("LOG", aid);
            downloadBase64File(file, `${data?.form?.refno}_LOG.pdf`);
          }} title={`${data?.form?.refno}_LOG.pdf`}>{t("translation:admin.request.tab.claim.view_log")}</DownloadBtn>

          <FormButtonPrimary size={"extraSmall"} onClick={async () => {
            await loadEmailTemplate(aid, "RESENDLOG");

            openModalConfirm({
              title: t("translation:admin.request.tab.email.compose_email"),
              modalConfirmPrimaryButtonText: t("send"),
              modalConfirmSecondaryButtonText: t("back"),
              content: <ContentComposeEmail disabled={true} />,
              closeManually: true,
              style: { content: { width: 720 } },
              callback: () => {
                const { contentComposeEmail_Title, setIsValidEmailTitle } = useAdminApplicationDetailStore.getState();
                const { admSendEmail } = useAdminApplicationDetailStore.getState();

                const loadAdmSendEmail = withErrorHandling(admSendEmail);

                if (!contentComposeEmail_Title) {
                  setIsValidEmailTitle(false);
                } else {
                  loadAdmSendEmail(aid).then(() => {
                    closeModalConfirm();
                    loadAdmGetPreAuthForm(aid).then(() => {
                      openModalInfo({
                        title: "Compose Email",
                        isShowSuccessIcon: true,
                        text: <FormText>Email has been sent to : <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span></FormText>,
                        style: { content: { width: 480 } },
                      });
                    });
                  });
                }
              },
              cancelCallback: () => {

              },
            });
          }}>
            <Icon20 src={`${process.env.PUBLIC_URL}/images/email2_white.png`} alt="Resend LOG" />
            <FormText style={{ color: "var(--aia-bx-white)" }}>Resend LOG</FormText>
          </FormButtonPrimary>
        </Flex>
      </Flex>

      <Refno>Reference No.：{data?.form?.refno}</Refno>

      <Flex justifyContent={"flex-start"} alignItems={"flex-start"} gap={40} style={{ flexWrap: "wrap", minWidth: 600 }}>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Approved By</FormRemark>
          <FormText>{data?.form?.approveByUser}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Entitled Room</FormRemark>
          <FormText>{data?.claim?.lvOfAccommodation}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Approved Date</FormRemark>
          <FormText>{data?.form?.approvedt?.substr(0, 10) ?? "-"}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Last Update</FormRemark>
          <FormText>{data?.form?.moddt?.substr(0, 10) ?? "-"}</FormText>
        </Flex>
      </Flex>

      <Flex justifyContent={"flex-start"} gap={16}>
        {
          getProgrssStatus({
            step: 1,
            intstatus: data?.form?.intstatus,
            flowType: data?.form?.flowType,
          })
        }
        <HDivider />
        {
          getProgrssStatus({
            step: 2,
            intstatus: data?.form?.intstatus,
            flowType: data?.form?.flowType,
          })
        }
        <HDivider />
        {
          getProgrssStatus({
            step: 3,
            intstatus: data?.form?.intstatus,
            flowType: data?.form?.flowType,
          })
        }
      </Flex>

      <Divider style={{ width: "100%" }} marginVertical={0} />

      <Flex gap={8} justifyContent={"space-between"} width={"100%"}>
        <Flex gap={8}>
          <Icon32 src={`${process.env.PUBLIC_URL}/images/documents-3pcs.png`} alt="Claim Details" />
          <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>Claim Details</FormTitle>
        </Flex>

        {
          ((intstatus?.value === "PENDCHARGE" || intstatus?.value === "OUTCHARGE") && !isCSTeam()) &&
          <FormButtonDanger size={"extraSmall"} style={{ marginRight: 8 }} onClick={() => {
            openContentScheduledChargeback(aid, { value: "SCHCHARGE" });
          }}>
            <Flex>
              <Icon16 src={`${process.env.PUBLIC_URL}/images/money-bag-white.png`} alt="Chargeback" />
              <FormText style={{ color: "var(--aia-bx-white)" }}>Chargeback</FormText>
            </Flex>
          </FormButtonDanger>
        }
      </Flex>

      <Flex justifyContent={"flex-start"} alignItems={"flex-start"} style={{ flexWrap: "wrap", minWidth: 600, columnGap: 40, rowGap: 16 }}>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Treatment Type</FormRemark>
          <FormText>{data?.claim?.treatmentType ?? "-"}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Classification of Surgery</FormRemark>
          <FormText>{data?.claim?.classificationSurgery ?? "-"}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Provide ICD Code</FormRemark>
          <FormText>
            {
              data?.claim?.ICDCode ?
                <Tooltip placement="left" title={data?.claim?.ICDCode}>
                  <View>View</View>
                </Tooltip>
                :
                <Tooltip placement="left" title={data?.claim?.ICDDescription ?? "-"}>
                  <View>View</View>
                </Tooltip>
            }
          </FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Remarks</FormRemark>
          <FormText>
            <Tooltip placement="left" title={getRemarkString(data?.claim?.treatmentRemarkOption, data?.claim?.treatmentRemark)}>
              <View>View</View>
            </Tooltip>
          </FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Saving Amount</FormRemark>
          <FormText>HK$ {formatCurrency(data?.claim?.saveAmt) ?? "-"}</FormText>
        </Flex>

        {
          !isCSTeam() &&
          <Flex direction={"column"} alignItems={"flex-start"}>
            <FormButtonSecondary size={"extraSmall"} title={"Edit"} onClick={() => {
              openContentApprove1Modal();
            }}>
              <Flex>
                <Icon16 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt="edit" />
                <FormText style={{ color: "var(--aia-bx-dark-blue)" }}>Edit</FormText>
              </Flex>
            </FormButtonSecondary>
          </Flex>
        }
      </Flex>

      <Flex justifyContent={"flex-start"} alignItems={"flex-start"} style={{ flexWrap: "wrap", minWidth: 600, columnGap: 40, rowGap: 16 }}>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>LOG Amount</FormRemark>
          <FormText>HK$ {formatCurrency(data?.claim?.LOGAmt) ?? "-"}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Hold Amount</FormRemark>
          <FormText>HK$ {formatCurrency(5000) ?? "-"}</FormText>
        </Flex>

        {
          (intstatus?.value === "SCHCHARGE" || intstatus?.value === "CHARGESETTLE" || intstatus?.value === "OUTCHARGE" || intstatus?.value === "CLAIMSETTLE") &&
          <Flex direction={"column"} alignItems={"flex-start"}>
            <FormRemark>Chargeback Date & Time</FormRemark>
            <FormText>{data?.claim?.chargeBackDt?.substr(0, 10)} {data?.claim?.chargeBackTime}</FormText>
          </Flex>
        }
      </Flex>

      <Flex justifyContent={"flex-start"} alignItems={"flex-start"} style={{ flexWrap: "wrap", minWidth: 600, columnGap: 40, rowGap: 16 }}>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Incurred Amount</FormRemark>
          {
            data?.claim?.incurredAmt === null ?
              <FormText>-</FormText> :
              <FormText>HK$ {formatCurrency(data?.claim?.incurredAmt) ?? "-"}</FormText>
          }
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Eligible Amount</FormRemark>
          {
            data?.claim?.paidAmt === null ?
              <FormText>-</FormText> :
              <FormText>HK$ {formatCurrency(data?.claim?.paidAmt) ?? "-"}</FormText>
          }
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Chargeback</FormRemark>
          {
            data?.claim?.chargeBack === null ?
              <FormText>-</FormText> :
              <FormText>HK$ {formatCurrency(data?.claim?.chargeBack) ?? "-"}</FormText>
          }
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>WIS No.</FormRemark>
          <FormText>{data?.claim?.WISNo ?? "-"}</FormText>
        </Flex>
        <Flex direction={"column"} alignItems={"flex-start"}>
          <FormRemark>Debit no.</FormRemark>
          <FormText>{data?.claim?.debitno ?? "-"}</FormText>
        </Flex>

        {
          (!isCSTeam() && (intstatus?.value === "PENDCHARGE" || intstatus?.value === "SCHCHARGE" || intstatus?.value === "CHARGESETTLE" || intstatus?.value === "OUTCHARGE" || intstatus?.value === "CLAIMSETTLE")) &&
          <Flex direction={"column"} alignItems={"flex-start"}>
            <FormButtonSecondary size={"extraSmall"} title={"Edit"} onClick={() => {
              openContentClaimRequest();
            }}>
              <Flex>
                <Icon16 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt="edit" />
                <FormText style={{ color: "var(--aia-bx-dark-blue)" }}>Edit</FormText>
              </Flex>
            </FormButtonSecondary>
          </Flex>
        }
      </Flex>

      <Divider style={{ width: "100%" }} marginVertical={0} />

      <Flex gap={8}>
        <Icon32 src={`${process.env.PUBLIC_URL}/images/training2.png`} alt="Progress Details" />
        <FormTitle style={{ fontSize: 20, lineHeight: "30px" }}>Progress Details</FormTitle>
      </Flex>

      <Flex gap={8} direction={"column"} alignItems={"flex-start"}>
        {
          data?.workflow?.map((item) => {
            return <Flex gap={16} key={item.wkid}>
              <Log>{item.wkmsg}</Log>
              {item.actionby && <LogModby>{item.actionby}</LogModby>}
              <LogDatetime>{item.wkactiondt}</LogDatetime>
            </Flex>
          })
        }

        {
          data?.workflow?.length === 0 && <Log>No progress details</Log>
        }
      </Flex>
    </Flex>
  );
}

const Icon32 = styled.img`
  width: 32px;
  height: 32px;
`;

const Icon20 = styled.img`
  width: 20px;
  height: 20px;
`;

const Icon16 = styled.img`
  width: 16px;
  height: 16px;
`;

const IconProgress = styled.div`
  width: 20px;
  height: 20px;
  background-color: var(--aia-bx-gray-4);
  color: var(--aia-bx-gray-2);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 10px;
`;

const TextGreen = styled(FormText)`
  color: var(--aia-bx-green-60);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

const TextBlue = styled(FormText)`
  color: var(--aia-bx-dark-blue);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

const TextRed = styled(FormText)`
  color: var(--aia-bx-red-1);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

const Refno = styled(FormText)`
  font-size: 12px;
  font-weight: bold;
  line-height: 18px;
  color: var(--aia-bx-dark-blue);
`;

const HDivider = styled.div`
  background-color: var(--aia-bx-dark-blue);
  width: 30px;
  height: 1px;
`;

const Log = styled(FormText)`
  color: var(--aia-bx-light-black);
  font-size: 12px;
  line-height: 18px;
  font-weight: bold;
`;

const LogModby = styled(FormRemark)`
  font-size: 12px;
  line-height: 18px;
`;

const LogDatetime = styled(FormRemark)`
  font-size: 12px;
  line-height: 18px;
`;

const View = styled.div`
  color: var(--aia-bx-blue-2);
  cursor: pointer;
  text-decoration: underline;
`;

const DownloadBtn = styled.button`
  flex-shrink: 0;
  padding: 7.5px 20px;
  background-color: var(--aia-bx-white);
  color: var(--aia-bx-dark-blue);
  border: 1px solid var(--aia-bx-blue-2);
  border-radius: 100px;
  cursor: pointer;
  font-size: 14px;
  visibility: visible;
  justify-content: center;
  text-decoration: none;
  
  display: flex;
  align-items: center;
  
  ${props => props.$disabled ? `
      cursor: not-allowed;
      opacity: 0.3;
      background-color: var(--aia-bx-gray-5);
      border-color: var(--aia-bx-gray-5);
      color: var(--aia-bx-gray-4);
    `
    :
    `
    &:hover {
      background-color: var(--aia-bx-blue-5);
    }
  `
  };
`;

export default Claim;